/**
 * API client for a bounty owner's actions on submissions, wrapping the
 * backend /users/:userId/bounties/:bountyId/submissions/* endpoints.
 * Lists submissions to a bounty, accepts a submission (triggering payment
 * with a Stripe payment method), polls payment status by idempotency key,
 * rejects submissions, and fetches the accepted submission.
 * Consumed by ItemCollection, SubmissionDisplay, and handlePaymentMethodCreated.
 */
import {
    PollSubmissionPaymentStatusResponse,
    ProcessAcceptSubmissionPaymentResponse,
    SubmissionStatus,
} from "@/types/submission";
import { PaginatedResponse } from "@/types/pagination";
import { SubmissionWithBounty, SubmissionDisplay } from "@/types/submission";
import { fetchPaginatedItems } from "@/api/paginated";
import { api } from "@/lib/axios";

export interface UserBountySubmissionQueryParams {
    page?: number;
    perPage?: number;
    status?: SubmissionStatus;
}

export const UserBountySubmissionAPI = {
    fetchUserBountySubmissions: async (
        userId: number,
        bountyId: number,
        params: UserBountySubmissionQueryParams = {}
    ): Promise<PaginatedResponse<SubmissionWithBounty>> => {
        return fetchPaginatedItems<SubmissionWithBounty>(
            `/users/${userId}/bounties/${bountyId}/submissions`,
            params
        );
    },
    getSubmissionPaymentStatus: async (
        userId: number,
        bountyId: number,
        submissionId: number,
        idempotencyKey: string
    ): Promise<PollSubmissionPaymentStatusResponse> => {
        const { data } = await api.get<PollSubmissionPaymentStatusResponse>(
            `/users/${userId}/bounties/${bountyId}/submissions/${submissionId}/payment-status/${idempotencyKey}`
        );
        return data;
    },
    acceptSubmission: async (
        userId: number,
        bountyId: number,
        submissionId: number,
        paymentMethodId: string
    ) => {
        const { data } = await api.post<ProcessAcceptSubmissionPaymentResponse>(
            `/users/${userId}/bounties/${bountyId}/submissions/${submissionId}/accept`,
            {
                paymentMethodId,
            }
        );
        return data;
    },
    rejectSubmission: async (userId: number, bountyId: number, submissionId: number) => {
        return api.post(`/users/${userId}/bounties/${bountyId}/submissions/${submissionId}/reject`);
    },
    getAcceptedSubmission: async (userId: number, bountyId: number) => {
        const { data } = await api.get<{ submission: SubmissionDisplay }>(
            `/users/${userId}/bounties/${bountyId}/submissions/accepted`
        );
        return data;
    },
};
